import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import FloatingAIButton from "./components/FloatingAIButton";
import cab from "./assests/cab.png";
import Modal from "./Modal";
import API_BASE_URL from "./config/api";

function Cab() {
  const location = useLocation();
  const navigate = useNavigate();
  const { packageId, dayIndex } = location.state || {};
  const [selectedDayIndex, setSelectedDayIndex] = useState(dayIndex || 0);
  const destinations = location.state?.itineraryDetails?.destinations || [];
  const destination = destinations?.[selectedDayIndex]?.name || "Munnar";

  const [cabs, setCabs] = useState([]);
  const [selectedCab, setSelectedCab] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [packageDetails, setPackageDetails] = useState(null);

  useEffect(() => {
    const fetchCabs = async () => {
      const requestOptions = {
        method: "GET",
        redirect: "follow",
      };

      try {
        const response = await fetch(
          `${API_BASE_URL}/cabs/destination?destination=${destination}`,
          requestOptions
        );
        const result = await response.json();
        setCabs(result);
      } catch (error) {
        console.error("Error fetching cabs:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchPackageDetails = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/packages/${packageId}`,
          { method: "GET" }
        );
        const result = await response.json();
        setPackageDetails(result);
      } catch (error) {
        console.error("Error fetching package details:", error);
      }
    };

    fetchCabs(); // **Call fetchCabs to load the cabs**

    if (packageId) {
      fetchPackageDetails();
    }
  }, [packageId, destination]);

  const handleSelectCab = async (cabId) => {
    setSelectedCab(cabId);
    if (packageDetails) {
      const updatedPackage = {
        ...packageDetails,
        itinerary: packageDetails.itinerary.map((day, index) => {
          if (index === selectedDayIndex) {
            return {
              ...day,
              cabs: [
                {
                  cabId: { _id: cabId },
                  days: day.cabs?.[0]?.days || 1, // Keep existing days or default to 1
                },
              ],
            };
          }
          return day;
        }),
      };

      try {
        setUpdating(true);
        const response = await fetch(
          `${API_BASE_URL}/packages/${packageId}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatedPackage),
          }
        );

        if (response.ok) {
          navigate(`/tripdetails?id=${packageId}`);
        } else {
          console.error("Error updating package:", await response.text());
          alert("Failed to update the package.");
        }
      } catch (error) {
        console.error("Error updating package:", error);
        alert("An error occurred while updating the package.");
      } finally {
        setUpdating(false);
      }
    }
  };

  const openModal = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  if (loading) {
    return <div style={styles.container}>Loading cabs...</div>;
  }

  return (
    <>
      <Navbar />
      <div style={styles.destinationPage}>
        <header style={styles.destinationHeader}>
          <div style={styles.leftContent}>
            <h1 style={styles.h1}>Cabs</h1>
          </div>
          <div>
            <img
              src={cab}
              alt="cab image"
              style={{
                width: 1350,
                height: 300,
                objectFit: "cover",
                objectPosition: "center",
                position: "relative",
              }}
            />
          </div>
        </header>
        <div
          style={{
            color: "#004E61",
            fontFamily: "Merriweather",
            fontSize: "45px",
            fontWeight: "700",
            lineHeight: "50px",
            textAlign: "center",
          }}
        >
          Available Cabs in {destination}
        </div>
      </div>
      <div style={styles.container}>
        <div style={styles.cabList}>
          {cabs.length > 0 ? (
            cabs.map((cab) => (
              <div key={cab._id} style={styles.cabCard}>
                <h2 style={styles.cabName}>{cab.name}</h2>
                <p style={styles.cabDetails}>Type: {cab.type}</p>
                <p style={styles.cabDetails}>Seats: {cab.seats}</p>
                <p style={styles.cabDetails}>
                  Price per Day: ₹{cab.pricePerDay}
                </p>
                <p style={styles.cabDetails}>
                  Availability: {cab.available ? "Available" : "Not Available"}
                </p>
                {cab.seasonalRates.length > 0 && (
                  <div>
                    <h3 style={styles.cabDetails}>Seasonal Rates:</h3>
                    {cab.seasonalRates.map((rate) => (
                      <p key={rate._id} style={styles.cabDetails}>
                        {rate.season}: ₹{rate.pricePerDay}
                      </p>
                    ))}
                  </div>
                )}
                <button
                  style={styles.selectButton}
                  onClick={() => handleSelectCab(cab._id)}
                  disabled={updating}
                >
                  {updating && selectedCab === cab._id
                    ? "Updating..."
                    : "Select"}
                </button>
              </div>
            ))
          ) : (
            <p style={styles.cabDetails}>No cabs available for {destination}</p>
          )}
        </div>
      </div>
      {/* Floating box with image */}
      <FloatingAIButton onClick={openModal} />
      {/* Modal */}
      {isModalOpen && <Modal closeModal={closeModal} />}
      <Footer />
    </>
  );
}

const styles = {
  destinationPage: {
    padding: "20px",
    fontFamily: "Arial, sans-serif",
  },
  destinationHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
    marginBottom: "20px",
  },
  leftContent: {
    position: "absolute",
    left: "100px",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 2,
    color: "white",
  },
  h1: {
    fontFamily: '"Merriweather", serif',
    fontSize: "70px",
    fontWeight: 400,
    marginTop: "-30px",
    marginLeft: "-40px",
    color: "#004e61",
  },
  breadcrumb: {
    fontSize: "27px",
    fontWeight: 300,
    fontFamily: '"Merriweather", serif',
    color: "#1ca8cb",
    marginTop: "5%",
  },
  breadcrumbLink: {
    textDecoration: "none",
    color: "#1ca8cb",
  },
  rightImage: {
    width: "96vw",
    height: "300px",
    objectFit: "cover",
    filter: "brightness(50%)",
    position: "relative",
  },
  container: {
    padding: "20px",
    fontFamily: "Arial, sans-serif",
  },
  cabList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "20px",
  },
  cabCard: {
    border: "1px solid #D9D9D9",
    borderRadius: "15px",
    padding: "20px",
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
  },
  cabName: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#004E61",
    marginBottom: "10px",
  },
  cabDetails: {
    fontSize: "14px",
    fontWeight: "300",
    color: "#616161",
    marginBottom: "10px",
  },
  selectButton: {
    backgroundColor: "#004E61",
    color: "#fff",
    padding: "10px 20px",
    border: "none",
    borderRadius: "30px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "400",
    fontFamily: "Merriweather",
    marginTop: "10px",
  },
  floatingBox: {
    position: "fixed",
    bottom: "20px",
    right: "115px",
    width: "96px",
    height: "96px",
    display: "flex",
    borderRadius: "15px",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
  },
  floatingButton: {
    border: "none",
    backgroundColor: "transparent",
    padding: 0,
    cursor: "pointer",
    borderRadius: "15px",
  },
  floatingImage: {
    width: "96px",
    height: "96px",
    objectFit: "cover",
    borderRadius: "15px",
  },
};

export default Cab;
